/**
 * Route table and guards. Three layers decide what renders:
 *   1. public pages (`/l/:token`, `/q/:token`, `/share/:token`, `/legal/:doc`, `/account/revert`, `/account/google-revert`, `/agent/change`) render for anyone, signed in or not;
 *   2. session status — sign-in, org setup, or the signed-in shell;
 *   3. role — a route whose capability the live role lacks shows a
 *      "not for your role" page instead of a screen that would 403.
 */
import type { ReactNode } from 'react';
import { can, ROLE_LABELS, type Capability } from './auth/roles';
import { useSession } from './auth/session';
import { TermsReviewProvider } from './legal/TermsGate';
import { LeadBrief } from './pages/LeadBrief';
import { HomeownerQuote } from './pages/HomeownerQuote';
import { LegalPage } from './pages/LegalPage';
import { AgentDocs } from './pages/AgentDocs';
import { SignupReport } from './pages/SignupReport';
import { InviteAccept } from './pages/InviteAccept';
import { EmailRevert } from './pages/EmailRevert';
import { GoogleLinkRevert } from './pages/GoogleLinkRevert';
import { AgentChangeLink } from './pages/AgentChangeLink';
import { Claim } from './screens/Claim';
import { AgentAccess } from './screens/AgentAccess';
import { OAuthConsent } from './screens/OAuthConsent';
import { SharedDashboard } from './pages/SharedDashboard';
import { Link, matchPath, useRouter } from './router';
import { Account } from './screens/Account';
import { safeNext, signInFor } from './nextPath';

export { safeNext, signInFor };
import { Audit } from './screens/Audit';
import { Branding } from './screens/Branding';
import { ConfigEditor } from './screens/ConfigEditor';
import { Contractors, ContractorsPage } from './screens/Contractors';
import { Dashboard } from './screens/Dashboard';
import { DestinationDetail, Destinations } from './screens/Destinations';
import { Inbox, LeadDetail } from './screens/Inbox';
import { Install } from './screens/Install';
import { InstallOwner } from './screens/InstallOwner';
import { OpsPartnerDetail, OpsPartners, OpsPending } from './screens/Ops';
import { OpsBilling } from './screens/OpsBilling';
import { Billing } from './screens/Billing';
import { HostedPageProfile } from './screens/HostedPageProfile';
import { Pages } from './screens/Pages';
import { OrgSetup } from './screens/OrgSetup';
import { RoutingMode } from './screens/RoutingMode';
import { RoutingRules } from './screens/RoutingRules';
import { NETWORK_FALLBACK_ANCHOR } from './screens/NetworkFallback';
import { Settings } from './screens/Settings';
import { SignIn } from './screens/SignUp';
import { Sites } from './screens/Sites';
import { Team } from './screens/Team';
import { TestLead } from './screens/TestLead';
import { TradesArea } from './screens/TradesArea';
import { Verify } from './screens/Verify';
import { Shell } from './Shell';
import { ShellSkeleton } from './ShellSkeleton';
import { titleFor, useDocumentTitle } from './pageTitle';
import { ProgressProvider } from './setup/progress';
import { SitesProvider } from './state/sites';
import { Empty, Loading } from './ui/kit';

interface RouteDef {
  pattern: string;
  capability: Capability;
  /** Calm, narrow column (the onboarding register). */
  onboarding?: boolean;
  render: (params: Record<string, string>) => ReactNode;
}

export const ROUTES: RouteDef[] = [
  { pattern: '/setup/trades', capability: 'editConfig', onboarding: true, render: () => <TradesArea /> },
  { pattern: '/setup/installer', capability: 'members', onboarding: true, render: () => <InstallOwner /> },
  { pattern: '/setup/install', capability: 'sites', onboarding: true, render: () => <Install /> },
  { pattern: '/setup/verify', capability: 'sites', onboarding: true, render: () => <Verify /> },
  { pattern: '/setup/branding', capability: 'editConfig', render: () => <Branding /> },
  { pattern: '/setup/routing', capability: 'destinations', onboarding: true, render: () => <RoutingMode /> },
  { pattern: '/setup/contractors', capability: 'roster', onboarding: true, render: () => <Contractors /> },
  // No longer a setup step (nothing for the partner to do): old links land on its row in Organisation.
  { pattern: '/setup/fallback', capability: 'readOrg', render: () => <Redirect to={`/settings#${NETWORK_FALLBACK_ANCHOR}`} /> },
  { pattern: '/setup/test', capability: 'leads', onboarding: true, render: () => <TestLead /> },
  { pattern: '/dashboard', capability: 'funnel', render: () => <Dashboard /> },
  { pattern: '/leads', capability: 'leads', render: () => <Inbox /> },
  { pattern: '/leads/:id', capability: 'leads', render: (p) => <LeadDetail id={p.id ?? ''} /> },
  { pattern: '/sites', capability: 'sites', render: () => <Sites /> },
  { pattern: '/config', capability: 'editConfig', render: () => <ConfigEditor /> },
  { pattern: '/contractors', capability: 'roster', render: () => <ContractorsPage /> },
  { pattern: '/routing', capability: 'routing', render: () => <RoutingRules /> },
  { pattern: '/destinations', capability: 'destinations', render: () => <Destinations /> },
  { pattern: '/destinations/:id', capability: 'destinations', render: (p) => <DestinationDetail id={p.id ?? ''} /> },
  { pattern: '/team', capability: 'members', render: () => <Team /> },
  { pattern: '/audit', capability: 'audit', render: () => <Audit /> },
  { pattern: '/settings', capability: 'readOrg', render: () => <Settings /> },
  // You, not the organisation: sign-in email and methods. Every role has one.
  { pattern: '/account', capability: 'readOrg', render: () => <Account /> },
  { pattern: '/agent', capability: 'agentTokens', render: () => <AgentAccess /> },
  // An account an AI agent signed up (migration 080): the named person
  // confirms here. Every role may look; the server lets only them confirm.
  { pattern: '/claim', capability: 'readOrg', onboarding: true, render: () => <Claim /> },
  // MCP OAuth consent (T097): reached from the API's authorize endpoint; a
  // signed-out visit signs in first and comes back here via `next`.
  { pattern: '/oauth/consent', capability: 'agentTokens', onboarding: true, render: () => <OAuthConsent /> },
  // Contractor pages: the hosted page's full profile (phase 1) and billing (phase 2).
  { pattern: '/pages', capability: 'hostedPage', render: () => <Pages /> },
  { pattern: '/pages/:slug', capability: 'hostedPageProfile', render: (p) => <HostedPageProfile slug={p.slug ?? ''} /> },
  { pattern: '/billing', capability: 'billing', render: () => <Billing /> },
  // WS-A2 additions below
  // WS-B2 additions below
  // WS-C2 additions below
];

/**
 * FinderlyFix admin (migration 076): gated on `whoami.is_operator`, not on the
 * organisation role — an operator is FinderlyFix staff across every
 * organisation. Anyone else gets the ordinary "Page not found", and the
 * server's `/partner/ops/*` answers 404 regardless.
 */
export const OPS_ROUTES: Array<{ pattern: string; render: (params: Record<string, string>) => ReactNode }> = [
  { pattern: '/ops', render: () => <OpsPending /> },
  { pattern: '/ops/partners', render: () => <OpsPartners /> },
  { pattern: '/ops/partners/:id', render: (p) => <OpsPartnerDetail id={p.id ?? ''} /> },
  { pattern: '/ops/billing', render: () => <OpsBilling /> },
];

/** Where "/" lands, by role: owners/admins start at setup, operators in the inbox. */
export function homeFor(role: ReturnType<typeof useSession>['role']): string {
  if (can(role, 'editConfig')) return '/dashboard';
  if (can(role, 'leads')) return '/leads';
  if (can(role, 'sites')) return '/setup/install';
  if (can(role, 'funnel')) return '/dashboard';
  return '/settings';
}

function Redirect({ to }: { to: string }) {
  const { navigate } = useRouter();
  queueMicrotask(() => navigate(to, { replace: true }));
  return null;
}

/** The signed-in routes, with the Partner Terms modal over them when the org owes an acceptance. */
function SignedIn() {
  return (
    <TermsReviewProvider>
      <SignedInRoutes />
    </TermsReviewProvider>
  );
}

function SignedInRoutes() {
  const { role, org, whoami } = useSession();
  const { path, search } = useRouter();
  if (path === '/sign-in') return <Redirect to={safeNext(search.get('next')) ?? homeFor(role)} />;
  if (path === '/' || path === '/welcome') return <Redirect to={homeFor(role)} />;
  if (whoami?.is_operator) {
    for (const r of OPS_ROUTES) {
      const params = matchPath(r.pattern, path);
      if (params) return <Shell>{r.render(params)}</Shell>;
    }
  }
  for (const r of ROUTES) {
    const params = matchPath(r.pattern, path);
    if (!params) continue;
    if (!can(role, r.capability)) {
      return (
        <Shell>
          <Empty title="Not available for your role">
            You are signed in as {role ? ROLE_LABELS[role] : 'a member'} of {org?.display_name}. Ask an owner or admin if you need access.
          </Empty>
        </Shell>
      );
    }
    return <Shell onboarding={r.onboarding}>{r.render(params)}</Shell>;
  }
  return (
    <Shell>
      <Empty level={2} title="Page not found">
        <p style={{ margin: '0 0 12px' }}>There’s nothing at {path}. It may have moved, or it isn’t available yet.</p>
        <Link className="btn btn-primary" to={homeFor(role)}>
          {homeFor(role) === '/dashboard' ? 'Back to dashboard' : 'Back to the portal'}
        </Link>
      </Empty>
    </Shell>
  );
}

export function App() {
  const { status, org } = useSession();
  const { path, search } = useRouter();
  useDocumentTitle(titleFor(path));
  const brief = matchPath('/l/:token', path);
  if (brief) return <LeadBrief token={brief.token ?? ''} />;
  // The homeowner's quote and job-completion link (contractor pages phase 3):
  // public, the token is the credential, mounted like the lead brief.
  const quote = matchPath('/q/:token', path);
  if (quote) return <HomeownerQuote token={quote.token ?? ''} />;
  const shared = matchPath('/share/:token', path);
  if (shared) return <SharedDashboard token={shared.token ?? ''} />;
  const legal = matchPath('/legal/:doc', path);
  if (legal) return <LegalPage doc={legal.doc ?? ''} />;
  // Partner docs for AI-assisted setup (migration 070): public, like /legal.
  if (path === '/docs/ai-setup') return <AgentDocs />;
  // The "This wasn't us" link from the agent sign-up notice (migration 080): public.
  if (path === '/signup/report') return <SignupReport />;
  if (path === '/invite/accept') return <InviteAccept />;
  // The "This wasn't me" link from the email-changed notice (migration 086): public,
  // signed in or not — whoever changed the email may well be signed in here.
  if (path === '/account/revert') return <EmailRevert />;
  // The same for the Google-linked notice (migration 090).
  if (path === '/account/google-revert') return <GoogleLinkRevert />;
  // The Undo / Approve / Reject links in the AI agent change emails (migration 096): public.
  if (path === '/agent/change') return <AgentChangeLink />;
  // Keep the frame on screen while the session restores; the sign-in page has no frame.
  if (status === 'loading') return path === '/sign-in' ? <Loading label="Signing you in" /> : <ShellSkeleton />;
  if (status === 'signed_out') {
    // The claim link (`/claim?email=…`) keeps its email for the sign-in form.
    if (path === '/claim') {
      const email = search.get('email');
      return <Redirect to={`/sign-in?next=%2Fclaim${email ? `&email=${encodeURIComponent(email)}` : ''}`} />;
    }
    if (path !== '/sign-in' && path !== '/') return <Redirect to={signInFor(path, search)} />;
    return <SignIn />;
  }
  if (status === 'unscoped') return <OrgSetup />;
  return (
    <SitesProvider key={org?.id}>
      <ProgressProvider key={org?.id}>
        <SignedIn />
      </ProgressProvider>
    </SitesProvider>
  );
}
